import { useRef, useEffect, useState, useMemo } from "react";
import { Tile } from "./GalleryTile";
import type { SavedGalleryEntry } from "./SavedGallery";
import { stars } from "./db/drawing";

export type BuiltinGalleryEntry = {
  id: string;
  src: string;
  thumb: string;
  category?: string;
  saved?: SavedGalleryEntry;
};

interface Props {
  visible: boolean;
  items: BuiltinGalleryEntry[];
  onSelect: (entry: BuiltinGalleryEntry) => void;
  onClose: () => void;
}

export function BuiltinGallery({ visible, items, onSelect, onClose }: Props) {
  const [galleryPage, setGalleryPage] = useState(0);
  const modalRef = useRef<HTMLDivElement>(null);
  // close when clicking outside modal
  useEffect(() => {
    if (!visible) return;
    const handleClick = (e: MouseEvent) => {
      if (modalRef && modalRef.current === e.target) {
        onClose();
      }
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [visible, onClose]);
  const [selectedCategory, setSelectedCategory] = useState<string>("All");

  const galleryPageSize = 9;

  // Gallery categories & filter
  const galleryCategories = useMemo(
    () => [
      "All",
      ...new Set(
        items.map((i) => i.category).filter((c): c is string => Boolean(c))
      ),
    ],
    [items]
  );

  const onCategoryChange = (cat: string) => {
    setSelectedCategory(cat);
    setGalleryPage(0);
  };

  // filter + paginate
  const filtered = items.filter(
    (e) => selectedCategory === "All" || e.category === selectedCategory
  );
  const visibleItems = filtered.slice(0, (galleryPage + 1) * galleryPageSize);

  const gridRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Scroll to top when filter changes
  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.scrollTop = 0;
    }
  }, [selectedCategory]);

  // Ensure-overflow guard: if no overflow yet and there are more items, load more.
  useEffect(() => {
    if (!visible) return;
    const el = gridRef.current;
    if (!el) return;
    if (
      el.scrollHeight <= el.clientHeight &&
      visibleItems.length < filtered.length
    ) {
      setGalleryPage((p) => p + 1);
    }
  }, [visible, visibleItems.length, filtered.length]);

  // IO-based infinite scroll (works even before overflow exists).
  useEffect(() => {
    if (!visible) return;
    const root = gridRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel) return;

    let locked = false;
    const io = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (
          entry.isIntersecting &&
          !locked &&
          visibleItems.length < filtered.length
        ) {
          locked = true;
          setGalleryPage((p) => p + 1);
          // release lock after this microtask so IO can re-evaluate on next layout
          queueMicrotask(() => {
            locked = false;
          });
        }
      },
      {
        root,
        rootMargin: "0px 0px 200px 0px",
        threshold: 0,
      }
    );

    io.observe(sentinel);
    return () => io.disconnect();
  }, [visible, visibleItems.length, filtered.length]);

  if (!visible) return null;

  return (
    <div ref={modalRef} className="gallery-modal">
      {/* category filter */}
      {galleryCategories.length > 1 && (
        <select
          className="category-select"
          value={selectedCategory}
          onChange={(e) => onCategoryChange(e.target.value)}
        >
          {galleryCategories.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>
      )}

      <div
        ref={gridRef}
        style={{
          width: "92vw",
          maxWidth: 1200,
          height: "85vh",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          touchAction: "pan-y",
          overscrollBehavior: "contain",
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 12,
          padding: 4,
          boxSizing: "border-box",
        }}
      >
        {visibleItems.map((entry, idx) => (
          <Tile
            key={idx}
            entry={entry}
            onClick={() => onSelect(entry)}
            progress={entry.saved?.progress}
            // show stars only when fully completed
            stars={
              entry.saved?.progress === 1
                ? stars(entry.saved.drawing.hints)
                : undefined
            }
          />
        ))}
        <div ref={sentinelRef} style={{ height: 1 }} />
      </div>

      <div style={{ marginTop: 16, textAlign: "center" }}>
        <button
          onClick={onClose}
          style={{
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))",
            border: "1px solid rgba(255,255,255,0.18)",
            color: "#fff",
            padding: "8px 16px",
            fontFamily:
              "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
            fontSize: 14,
            cursor: "pointer",
            borderRadius: 10,
            transition: "transform 120ms ease, box-shadow 120ms ease",
            boxShadow: "0 2px 10px rgba(0,0,0,0.25)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-1px)";
            e.currentTarget.style.boxShadow = "0 6px 18px rgba(0,0,0,0.35)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.25)";
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
}
